import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Avatar,
  Chip,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Paper,
  Tooltip,
  Stack,
  Container,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Security,
  Fingerprint,
  Smartphone,
  Delete,
  Add,
  QrCode,
  Key,
  Shield,
  PhoneAndroid,
  Computer,
  Close,
  Check,
  Warning,
  Info,
  LightbulbOutlined,
} from "@mui/icons-material";
import { User } from "../../types/users";
import {
  useMFA,
  PasskeyCredential,
  TOTPDevice,
  QRCodeData,
} from "../../hooks/useMFA";
import { apiClient, clearCookie } from "../../api/apiClient";
import { getAndSetMyUser } from "../../pages/finish-login";

interface MFAManagementProps {
  user: User;
  mode?: "enrollment" | "management";
  onPasskeyAdded?: (passkey: PasskeyCredential) => void;
  onPasskeyRemoved?: (passkeyId: string) => void;
  onTOTPAdded?: (totp: TOTPDevice) => void;
  onTOTPRemoved?: (totpId: string) => void;
  onError?: (error: Error) => void;
  onEnrollmentComplete?: () => void;
}

const MFAManagement: React.FC<MFAManagementProps> = ({
  user,
  mode = "management",
  onPasskeyAdded,
  onPasskeyRemoved,
  onTOTPAdded,
  onTOTPRemoved,
  onError,
  onEnrollmentComplete,
}) => {
  const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false);
  const [isSettingUpTOTP, setIsSettingUpTOTP] = useState(false);
  const [isVerifyingTOTP, setIsVerifyingTOTP] = useState(false);
  const [isTogglingMFA, setIsTogglingMFA] = useState(false);
  const [fetchedUser, setFetchedUser] = useState<User | null>(user);

  const [passkeys, setPasskeys] = useState<PasskeyCredential[]>([]);
  const [totpDevices, setTOTPDevices] = useState<TOTPDevice[]>([]);

  const [totpSetup, setTotpSetup] = useState<QRCodeData | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpDeviceName, setTotpDeviceName] = useState("");
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showProceedToLogin, setShowProceedToLogin] = useState(false);

  useEffect(() => {
    setFetchedUser(user);
  }, [user]);

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: "passkey" | "totp" | null;
    itemId: string | null;
    itemName: string;
  }>({
    open: false,
    type: null,
    itemId: null,
    itemName: "",
  });

  const [toggleMFADialog, setToggleMFADialog] = useState<{
    open: boolean;
    action: "enable" | "disable" | null;
  }>({
    open: false,
    action: null,
  });

  const {
    isLoading: isLoadingDevices,
    error,
    clearError,
    loadMFADevices,
    registerPasskey,
    setupTOTP,
    verifyTOTPSetup,
    removePasskey,
    removeTOTP,
  } = useMFA();

  const loadDevices = useCallback(async () => {
    try {
      const devices = await loadMFADevices();
      setPasskeys(devices.passkeys);
      setTOTPDevices(devices.totpDevices);
    } catch (error) {
      onError?.(
        error instanceof Error ? error : new Error("Failed to load MFA devices")
      );
    }
  }, [loadMFADevices, onError]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleRegisterPasskey = useCallback(async () => {
    setIsRegisteringPasskey(true);
    clearError();

    try {
      const newPasskey = await registerPasskey(user, passkeys);
      setPasskeys((prev) => [...prev, newPasskey]);
      onPasskeyAdded?.(newPasskey);

      if (mode === "enrollment") {
        onEnrollmentComplete?.();
      }
    } catch (error) {
      onError?.(
        error instanceof Error ? error : new Error("Failed to register passkey")
      );
    } finally {
      setIsRegisteringPasskey(false);
    }
  }, [
    user,
    passkeys,
    registerPasskey,
    onPasskeyAdded,
    onError,
    clearError,
    mode,
    onEnrollmentComplete,
  ]);

  const handleRemovePasskey = useCallback(
    async (passkeyId: string) => {
      try {
        await removePasskey(passkeyId);
        setPasskeys((prev) => prev.filter((pk) => pk.id !== passkeyId));
        onPasskeyRemoved?.(passkeyId);
      } catch (error) {
        onError?.(
          error instanceof Error ? error : new Error("Failed to remove passkey")
        );
      }
    },
    [removePasskey, onPasskeyRemoved, onError]
  );

  const handleSetupTOTP = useCallback(async () => {
    setIsSettingUpTOTP(true);
    clearError();
    setTotpDeviceName("");
    setShowProceedToLogin(false);

    try {
      const result = await setupTOTP();
      setTotpSetup(result);
    } catch (error) {
      onError?.(
        error instanceof Error ? error : new Error("Failed to setup TOTP")
      );
    } finally {
      setIsSettingUpTOTP(false);
    }
  }, [setupTOTP, onError, clearError]);

  const handleVerifyTOTP = useCallback(async () => {
    if (!totpSetup) return;

    setIsVerifyingTOTP(true);
    clearError();

    try {
      const result = await verifyTOTPSetup(
        totpSetup.secret,
        totpCode,
        totpDeviceName || "Authenticator App"
      );

      if (result.success) {
        await loadDevices();
        setTotpSetup(null);
        setTotpCode("");
        setTotpDeviceName("");

        if (mode === "enrollment") {
          onEnrollmentComplete?.();
        } else {
          setShowBackupCodes(true);
          setShowProceedToLogin(true);
        }
      }
    } catch (error) {
      onError?.(
        error instanceof Error ? error : new Error("Failed to verify TOTP code")
      );
    } finally {
      setIsVerifyingTOTP(false);
    }
  }, [
    totpSetup,
    totpCode,
    totpDeviceName,
    verifyTOTPSetup,
    loadDevices,
    onError,
    clearError,
    mode,
    onEnrollmentComplete,
  ]);

  const handleRemoveTOTP = useCallback(
    async (deviceId: string) => {
      try {
        await removeTOTP(deviceId);
        setTOTPDevices((prev) =>
          prev.filter((device) => device.id !== deviceId)
        );
        onTOTPRemoved?.(deviceId);
      } catch (error) {
        onError?.(
          error instanceof Error
            ? error
            : new Error("Failed to remove TOTP device")
        );
      }
    },
    [removeTOTP, onTOTPRemoved, onError]
  );

  const handleToggleMFA = useCallback(async () => {
    const currentMFAEnabled = fetchedUser?.must_use_mfa || false;
    const hasMFAMethods = passkeys.length + totpDevices.length > 0;

    if (!currentMFAEnabled && !hasMFAMethods) {
      onError?.(
        new Error(
          "You must add at least one MFA method before enabling MFA requirement."
        )
      );
      return;
    }

    const action = currentMFAEnabled ? "disable" : "enable";
    setToggleMFADialog({ open: true, action });
  }, [fetchedUser?.must_use_mfa, passkeys.length, totpDevices.length, onError]);

  const confirmToggleMFA = useCallback(async () => {
    const action = toggleMFADialog.action;
    setToggleMFADialog({ open: false, action: null });

    setIsTogglingMFA(true);
    clearError();

    try {
      const response = await apiClient.post("/api/users/toggle-mfa/", {});

      setFetchedUser(response.data);

      if (response.data && !response.data.must_use_mfa) {
        clearCookie("mfa_ok");
      }

      await getAndSetMyUser();

      console.log(response.data);
    } catch (error) {
      onError?.(
        error instanceof Error ? error : new Error("Failed to toggle MFA")
      );
    } finally {
      setIsTogglingMFA(false);
      if (action === "enable") {
        window.location.href = "/logout";
      }
    }
  }, [toggleMFADialog.action, clearError, onError]);

  const cancelToggleMFA = useCallback(() => {
    setToggleMFADialog({ open: false, action: null });
  }, []);

  const openDeleteDialog = useCallback(
    (type: "passkey" | "totp", itemId: string, itemName: string) => {
      setDeleteDialog({
        open: true,
        type,
        itemId,
        itemName,
      });
    },
    []
  );

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({
      open: false,
      type: null,
      itemId: null,
      itemName: "",
    });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.itemId || !deleteDialog.type) return;

    try {
      if (deleteDialog.type === "passkey") {
        await handleRemovePasskey(deleteDialog.itemId);
      } else if (deleteDialog.type === "totp") {
        await handleRemoveTOTP(deleteDialog.itemId);
      }

      const remainingPasskeys =
        deleteDialog.type === "passkey"
          ? passkeys.filter((pk) => pk.id !== deleteDialog.itemId)
          : passkeys;
      const remainingTOTPs =
        deleteDialog.type === "totp"
          ? totpDevices.filter((device) => device.id !== deleteDialog.itemId)
          : totpDevices;

      if (
        fetchedUser?.must_use_mfa &&
        remainingPasskeys.length === 0 &&
        remainingTOTPs.length === 0
      ) {
        window.location.reload();
      }

      closeDeleteDialog();
    } catch (error) {}
  }, [
    deleteDialog,
    handleRemovePasskey,
    handleRemoveTOTP,
    closeDeleteDialog,
    passkeys,
    totpDevices,
    fetchedUser?.must_use_mfa,
  ]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPasskeyIcon = (type: string) => {
    return type === "platform" ? <Fingerprint /> : <Key />;
  };

  return (
    <Container maxWidth="lg" sx={{ background: "white", p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <Security />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {mode === "enrollment"
                ? "Set Up Multi-Factor Authentication"
                : "Set Up Additional Security"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {mode === "enrollment"
                ? "You need to set up multi-factor authentication to continue. Choose one or more methods below."
                : `Manage security methods for ${user.first_name} ${user.last_name} (${user.email})`}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* MFA Requirement Toggle */}
      {mode === "management" && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" fontWeight="semibold" gutterBottom>
                  MFA Requirement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fetchedUser?.organization_enforces_2fa ||
                  fetchedUser?.enforce_2fa_by_admin
                    ? "MFA is enforced by your organization and cannot be disabled."
                    : fetchedUser?.must_use_mfa
                    ? "MFA is currently required for your account."
                    : "MFA is currently optional for your account."}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={fetchedUser?.must_use_mfa || false}
                    onChange={handleToggleMFA}
                    disabled={
                      fetchedUser?.organization_enforces_2fa ||
                      fetchedUser?.enforce_2fa_by_admin ||
                      isTogglingMFA
                    }
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {isTogglingMFA ? "Updating..." : "Require MFA"}
                  </Typography>
                }
              />
            </Stack>
            {fetchedUser?.organization_enforces_2fa ||
              (fetchedUser?.enforce_2fa_by_admin && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Your organization has enabled mandatory MFA. This setting
                    cannot be changed.
                  </Typography>
                </Alert>
              ))}
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && !totpSetup && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Passkeys Section */}
        <Grid item xs={12} lg={6}>
          <Card elevation={2} sx={{ height: "fit-content" }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 3 }}
              >
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Fingerprint color="primary" />
                    <Typography variant="h6" fontWeight="semibold">
                      Passkeys
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Secure, passwordless authentication using your device's
                    biometrics or security keys.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={
                    isRegisteringPasskey ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Add />
                    )
                  }
                  onClick={handleRegisterPasskey}
                  disabled={isRegisteringPasskey}
                  sx={{ minWidth: 120 }}
                >
                  {isRegisteringPasskey ? "Adding..." : "Add"}
                </Button>
              </Stack>

              {isLoadingDevices ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : passkeys.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}
                >
                  <Key sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No passkeys registered
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get started by adding your first passkey for enhanced
                    security.
                  </Typography>
                </Paper>
              ) : (
                <List disablePadding>
                  {passkeys.map((passkey, index) => (
                    <React.Fragment key={passkey.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            {getPasskeyIcon(passkey.authenticatorType)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="subtitle2">
                                {passkey.displayName}
                              </Typography>
                              <Chip
                                label={
                                  passkey.authenticatorType === "platform"
                                    ? "Device"
                                    : "Key"
                                }
                                size="small"
                                color={
                                  passkey.authenticatorType === "platform"
                                    ? "primary"
                                    : "secondary"
                                }
                              />
                            </Stack>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Created {formatDate(passkey.createdAt)}
                              {passkey.lastUsed &&
                                ` • Last used ${formatDate(passkey.lastUsed)}`}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip
                            title={
                              fetchedUser?.must_use_mfa &&
                              passkeys.length === 1 &&
                              totpDevices.length === 0
                                ? "Cannot delete the last MFA method when MFA is required"
                                : "Remove passkey"
                            }
                          >
                            <span>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() =>
                                  openDeleteDialog(
                                    "passkey",
                                    passkey.id,
                                    passkey.displayName
                                  )
                                }
                                disabled={
                                  fetchedUser?.must_use_mfa &&
                                  passkeys.length === 1 &&
                                  totpDevices.length === 0
                                }
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < passkeys.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* TOTP Section */}
        <Grid item xs={12} lg={6}>
          <Card elevation={2} sx={{ height: "fit-content" }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 3 }}
              >
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Smartphone color="success" />
                    <Typography variant="h6" fontWeight="semibold">
                      Authenticator Apps
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Use apps like Google Authenticator, Authy, or 1Password to
                    generate time-based codes.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    isSettingUpTOTP ? <CircularProgress size={16} /> : <Add />
                  }
                  onClick={handleSetupTOTP}
                  disabled={isSettingUpTOTP}
                  sx={{ minWidth: 140 }}
                >
                  {isSettingUpTOTP ? "Setting up..." : "Add"}
                </Button>
              </Stack>

              {isLoadingDevices ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : totpDevices.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}
                >
                  <PhoneAndroid
                    sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No authenticator apps configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add an authenticator app for additional security.
                  </Typography>
                </Paper>
              ) : (
                <List disablePadding>
                  {totpDevices.map((device, index) => (
                    <React.Fragment key={device.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "success.light" }}>
                            <PhoneAndroid />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="subtitle2">
                                {device.name}
                              </Typography>
                              {device.verified && (
                                <Chip
                                  icon={<Check sx={{ fontSize: 16 }} />}
                                  label="Verified"
                                  size="small"
                                  color="success"
                                />
                              )}
                            </Stack>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Created {formatDate(device.createdAt)}
                              {device.lastUsed &&
                                ` • Last used ${formatDate(device.lastUsed)}`}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip
                            title={
                              fetchedUser?.must_use_mfa &&
                              totpDevices.length === 1 &&
                              passkeys.length === 0
                                ? "Cannot delete the last MFA method when MFA is required"
                                : "Remove authenticator"
                            }
                          >
                            <span>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() =>
                                  openDeleteDialog(
                                    "totp",
                                    device.id,
                                    device.name
                                  )
                                }
                                disabled={
                                  fetchedUser?.must_use_mfa &&
                                  totpDevices.length === 1 &&
                                  passkeys.length === 0
                                }
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < totpDevices.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* TOTP Setup Dialog */}
      <Dialog
        open={!!totpSetup}
        onClose={() => {
          setTotpSetup(null);
          setShowProceedToLogin(false);
        }}
        maxWidth="md"
        fullWidth
        container={() => document.body}
        PaperProps={{
          elevation: 3,
          sx: {
            zIndex: 9999,
            position: "relative",
          },
        }}
        sx={{
          zIndex: 9999,
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9998,
          },
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <QrCode color="success" />
              <Typography variant="h6">Set up Authenticator App</Typography>
            </Stack>
            <IconButton onClick={() => setTotpSetup(null)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "white" }}>
          {/* Error Display within Dialog */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}

          {totpSetup && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box textAlign="center">
                  <Typography variant="h6" gutterBottom>
                    1. Scan QR Code
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 3, display: "inline-block", bgcolor: "white" }}
                  >
                    <img
                      src={totpSetup.qrCodeUrl}
                      alt="TOTP QR Code"
                      style={{
                        display: "block",
                        maxWidth: "200px",
                        height: "auto",
                      }}
                    />
                  </Paper>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Scan this QR code with your authenticator app
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  2. Enter Details
                </Typography>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Device Name (optional)"
                    value={totpDeviceName}
                    onChange={(e) => setTotpDeviceName(e.target.value)}
                    placeholder="My Phone"
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={totpCode}
                    onChange={(e) => {
                      setTotpCode(
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      );
                      if (error) clearError();
                    }}
                    placeholder="123456"
                    variant="outlined"
                    inputProps={{ maxLength: 6 }}
                    helperText="Enter the 6-digit code from your authenticator app"
                  />
                </Stack>
              </Grid>
            </Grid>
          )}

          {/* Backup Codes */}
          {showBackupCodes && totpSetup?.backupCodes && (
            <Alert severity="warning" sx={{ mt: 3 }} icon={<Warning />}>
              <AlertTitle>Save Your Backup Codes</AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Store these backup codes in a safe place. You can use them to
                access your account if you lose your authenticator device.
              </Typography>
              <Grid container spacing={1}>
                {totpSetup.backupCodes.map((code, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1,
                        textAlign: "center",
                        fontFamily: "monospace",
                        bgcolor: "grey.50",
                      }}
                    >
                      <Typography variant="body2">{code}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Button
                onClick={() => setShowBackupCodes(false)}
                sx={{ mt: 2 }}
                startIcon={<Check />}
              >
                I've saved these codes
              </Button>
            </Alert>
          )}

          {/* Proceed to Login Button */}
          {showProceedToLogin && (
            <Alert severity="success" sx={{ mt: 3 }} icon={<Check />}>
              <AlertTitle>Setup Complete!</AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Your authenticator app has been successfully set up.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setTotpSetup(null)}
                startIcon={<Check />}
              >
                Close
              </Button>
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setTotpSetup(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleVerifyTOTP}
            disabled={isVerifyingTOTP || totpCode.length !== 6}
            variant="contained"
            color="success"
            startIcon={
              isVerifyingTOTP ? <CircularProgress size={16} /> : <Check />
            }
          >
            {isVerifyingTOTP ? "Verifying..." : "Complete Setup"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <Typography variant="h6">Confirm Deletion</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this{" "}
            {deleteDialog.type === "passkey" ? "passkey" : "authenticator app"}?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This action cannot be undone. You will need to set up a new{" "}
              {deleteDialog.type === "passkey"
                ? "passkey"
                : "authenticator app"}{" "}
              if you want to use this method again.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeDeleteDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* MFA Toggle Confirmation Dialog */}
      <Dialog
        open={toggleMFADialog.open}
        onClose={cancelToggleMFA}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <Typography variant="h6">Confirm MFA Requirement</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to{" "}
            {toggleMFADialog.action === "enable" ? "enable" : "disable"} MFA
            requirement for your account?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {toggleMFADialog.action === "enable"
                ? "Enabling MFA requirement will log you out and require you to re-authenticate with MFA."
                : "Disabling MFA requirement will make your account less secure."}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={cancelToggleMFA} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmToggleMFA}
            variant="contained"
            color="warning"
            startIcon={<Warning />}
          >
            {toggleMFADialog.action === "enable" ? "Enable MFA" : "Disable MFA"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MFAManagement;
